/**
 * The ordered meeting points on a session type (OD-1325).
 *
 * A session type used to hold one meeting point, so the settings screen showed
 * `locations[0]` and offered "Add" only while that was empty. It can now hold
 * several — one primary, any number of secondaries — and this renders that list
 * with the operations the story calls for.
 *
 * Two rules are visible in the markup rather than hidden in a handler, because
 * they are the ones people get wrong:
 *
 *   - a meeting point a session is booked against is never deleted, only
 *     deactivated (AC-7). The button says Deactivate and the row stays,
 *     greyed, so the venue a participant was sent to remains on screen.
 *   - changing the primary does not move sessions already booked (AC-6). The
 *     caller says so in a toast at the moment it happens, since that is the
 *     assumption a scheduler would otherwise carry into the rest of the day.
 */
import {ArrowDown, ArrowUp, Edit, Plus} from 'lucide-react';
import {Button} from '@/shared/ui/button';

export interface MeetingPointRow {
  id: number;
  name?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  address?: string | null;
  instructions?: string | null;
  is_primary?: boolean;
  is_active?: boolean;
  display_order?: number;
  /** The venue's own IANA zone; every meeting point here must share one. */
  timezone?: string | null;
  /** Sessions booked against this venue; drives deactivate-vs-remove. */
  booked_session_count?: number;
}

interface MeetingPointsListProps {
  meetingPoints: MeetingPointRow[];
  isEditing: boolean;
  onAdd: () => void;
  onEdit: (point: MeetingPointRow) => void;
  onMakePrimary: (point: MeetingPointRow) => void;
  onToggleActive: (point: MeetingPointRow) => void;
  onRemove: (point: MeetingPointRow) => void;
  onMove: (point: MeetingPointRow, direction: -1 | 1) => void;
}

export const formatMeetingPointAddress = (point: MeetingPointRow): string => {
  const parts = [
    point.street_address,
    point.city,
    point.state,
    point.zip_code,
    point.country,
  ]
    .map((part) => (part || '').trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : point.address || '';
};

/** Primary first, then secondaries in order, deactivated last. */
export const sortMeetingPoints = (points: MeetingPointRow[]): MeetingPointRow[] =>
  [...points].sort((a, b) => {
    if (Boolean(b.is_primary) !== Boolean(a.is_primary)) {
      return Number(Boolean(b.is_primary)) - Number(Boolean(a.is_primary));
    }
    if (a.is_active !== b.is_active) {
      return Number(b.is_active !== false) - Number(a.is_active !== false);
    }
    return (a.display_order ?? 0) - (b.display_order ?? 0);
  });

const Badge = ({point}: {point: MeetingPointRow}) => {
  if (point.is_primary) {
    return (
      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
        Primary
      </span>
    );
  }
  if (point.is_active === false) {
    return (
      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
        Deactivated
      </span>
    );
  }
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
      Secondary
    </span>
  );
};

export const MeetingPointsList = ({
  meetingPoints,
  isEditing,
  onAdd,
  onEdit,
  onMakePrimary,
  onToggleActive,
  onRemove,
  onMove,
}: MeetingPointsListProps) => {
  const ordered = sortMeetingPoints(meetingPoints);
  const secondaries = ordered.filter((p) => !p.is_primary && p.is_active !== false);

  return (
    <div className="space-y-4 bg-white rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-semibold text-base">Meeting Points</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            The primary meeting point is used by default. Secondary meeting points can be
            chosen when sending self-booking invites. Slot capacity and moderator
            availability are shared across all meeting points.
          </p>
        </div>
        {isEditing && (
          <Button
            onClick={onAdd}
            className="gap-2 shrink-0 bg-black text-white hover:bg-black/90 rounded-lg px-4 py-2 h-auto"
          >
            <Plus className="h-4 w-4" />
            Add meeting point
          </Button>
        )}
      </div>

      {ordered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No meeting points assigned</p>
      ) : (
        <div className="space-y-3">
          {ordered.map((point, index) => {
            const inactive = point.is_active === false;
            const address = formatMeetingPointAddress(point);
            const secondaryIndex = secondaries.indexOf(point);
            const booked = point.booked_session_count ?? 0;
            // Timezone and booked-session count on one line: together they are
            // what makes a venue safe or unsafe to change, so they are read
            // together rather than hunted for separately.
            const meta = [
              point.timezone || null,
              booked > 0 ? `Used by ${booked} booked session${booked === 1 ? '' : 's'}` : null,
            ].filter(Boolean);
            return (
              <div
                key={point.id}
                className={`rounded-lg border p-4 ${
                  point.is_primary ? 'border-primary/40 bg-primary/5' : 'border-gray-200'
                } ${inactive ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      point.is_primary
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-base">
                        {point.name || 'Unnamed location'}
                      </p>
                      <Badge point={point} />
                    </div>
                    {address && <p className="mt-1 text-sm text-gray-700">{address}</p>}
                    {meta.length > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {meta.join(' · ')}
                      </p>
                    )}
                    {point.instructions && (
                      <p className="mt-2 text-sm text-gray-700">
                        Instructions: {point.instructions}
                      </p>
                    )}
                  </div>

                  {isEditing && (
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                      {!point.is_primary && !inactive && (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Move up"
                            disabled={secondaryIndex <= 0}
                            onClick={() => onMove(point, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Move down"
                            disabled={secondaryIndex === secondaries.length - 1}
                            onClick={() => onMove(point, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => onMakePrimary(point)}
                          >
                            Make primary
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Edit meeting point"
                        onClick={() => onEdit(point)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {!point.is_primary &&
                        (inactive ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => onToggleActive(point)}
                          >
                            Reactivate
                          </Button>
                        ) : (
                          /* A venue with sessions booked against it can only be
                             deactivated (AC-7); one with none can be removed
                             outright, since nothing depends on it resolving. */
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-destructive hover:text-destructive"
                            title={
                              booked > 0
                                ? "Used by booked sessions, so it can't be deleted"
                                : undefined
                            }
                            onClick={() =>
                              booked > 0 ? onToggleActive(point) : onRemove(point)
                            }
                          >
                            {booked > 0 ? 'Deactivate' : 'Remove'}
                          </Button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MeetingPointsList;
